import React, { useEffect, useState, useCallback } from "react";
import { api, API_BASE, euro, formatApiError } from "../lib/api";
import { Plus, FileText, Download, X, Trash2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";

let _itemKey = 0;
const newItem = (label = "", quantity = 1, unit_price = 0) => ({
  _key: `it-${++_itemKey}`, label, quantity, unit_price,
});

export default function Facturation() {
  const { user } = useAuth();
  const defaultPrice = user?.default_session_price ?? 60;
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ client_id: "", items: [newItem("Séance individuelle", 1, defaultPrice)], notes: "", service_date: "" });
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const [inv, cl] = await Promise.all([api.get("/invoices"), api.get("/clients")]);
      setInvoices(inv.data); setClients(cl.data);
      setForm((f) => f.client_id ? f : { ...f, client_id: cl.data[0]?.id || "" });
    } catch (e) {
      console.error("Failed to load invoices/clients", e);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const total = form.items.reduce((s, i) => s + Number(i.quantity || 0) * Number(i.unit_price || 0), 0);

  const setItem = (idx, key, value) => {
    setForm((f) => ({ ...f, items: f.items.map((it, i) => i === idx ? { ...it, [key]: value } : it) }));
  };
  const addItem = () => setForm((f) => ({ ...f, items: [...f.items, newItem("", 1, defaultPrice)] }));
  const removeItem = (idx) => setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

  const create = async () => {
    setError("");
    if (!form.client_id) return setError("Choisissez un client.");
    if (form.items.length === 0) return setError("Ajoutez au moins une ligne.");
    try {
      await api.post("/invoices", form);
      setShowCreate(false);
      setForm({ client_id: clients[0]?.id || "", items: [newItem("Séance individuelle", 1, defaultPrice)], notes: "", service_date: "" });
      load();
    } catch (e) { setError(formatApiError(e.response?.data?.detail)); }
  };

  const downloadPdf = async (id, number) => {
    const token = localStorage.getItem("mcb_token");
    const res = await fetch(`${API_BASE}/invoices/${id}/pdf`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${number}.pdf`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  const deleteInvoice = async (id) => {
    if (!window.confirm("Supprimer cette facture ?")) return;
    await api.delete(`/invoices/${id}`);
    load();
  };

  return (
    <div className="space-y-6" data-testid="facturation-container">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl">Facturation</h1>
          <p className="text-muted-soft mt-1">Vos factures, conformes et envoyées en un clic.</p>
        </div>
        <button onClick={() => setShowCreate(true)} className="btn-sage text-sm" data-testid="add-invoice-btn" disabled={clients.length === 0}>
          <Plus className="inline w-4 h-4 mr-1.5" strokeWidth={1.8} />
          Nouvelle facture
        </button>
      </div>

      {clients.length === 0 && (
        <div className="card-soft p-7 text-sm text-muted-soft" data-testid="no-clients-warning">
          Ajoutez d'abord un client depuis l'onglet Clients pour pouvoir émettre une facture.
        </div>
      )}

      {invoices.length === 0 ? (
        <div className="card-soft p-12 text-center" data-testid="invoices-empty">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-sage-light flex items-center justify-center mb-4">
            <FileText className="w-7 h-7 text-sage" strokeWidth={1.4} />
          </div>
          <div className="font-display text-xl mb-2">Aucune facture pour l'instant</div>
          <p className="text-muted-soft">La numérotation chronologique se fait automatiquement.</p>
        </div>
      ) : (
        <>
          {/* Mobile cards (visible < sm) */}
          <div className="sm:hidden space-y-3" data-testid="invoices-cards-mobile">
            {invoices.map((inv) => (
              <div key={inv.id} className="card-soft p-4" data-testid={`invoice-card-${inv.number}`}>
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium">{inv.number}</div>
                    <div className="text-sm text-muted-soft truncate">{inv.client_name}</div>
                    <div className="text-xs text-muted-soft mt-0.5">{(inv.issued_at || "").slice(0, 10)}</div>
                  </div>
                  <div className="font-display text-xl text-sage">{euro(inv.total)}</div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => downloadPdf(inv.id, inv.number)}
                    className="btn-sage text-sm flex-1"
                    data-testid={`invoice-pdf-${inv.number}`}
                  >
                    <Download className="inline w-4 h-4 mr-1.5" strokeWidth={1.8} />
                    Télécharger PDF
                  </button>
                  <button
                    onClick={() => deleteInvoice(inv.id)}
                    className="p-2.5 rounded-xl border"
                    style={{ borderColor: "var(--border)" }}
                    title="Supprimer"
                    data-testid={`invoice-delete-${inv.number}`}
                  >
                    <Trash2 className="w-4 h-4 text-muted-soft" strokeWidth={1.6} />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop table (visible ≥ sm) */}
          <div className="hidden sm:block card-soft p-2 sm:p-4 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-muted-soft">
                  <th className="text-left px-4 py-3 font-medium">Numéro</th>
                  <th className="text-left px-4 py-3 font-medium">Client</th>
                  <th className="text-left px-4 py-3 font-medium">Date</th>
                  <th className="text-right px-4 py-3 font-medium">Montant</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => (
                  <tr key={inv.id} className="border-t" style={{ borderColor: "var(--border)" }} data-testid={`invoice-row-${inv.number}`}>
                    <td className="px-4 py-4 font-medium">{inv.number}</td>
                    <td className="px-4 py-4">{inv.client_name}</td>
                    <td className="px-4 py-4 text-muted-soft">{(inv.issued_at || "").slice(0, 10)}</td>
                    <td className="px-4 py-4 text-right font-medium">{euro(inv.total)}</td>
                    <td className="px-4 py-4 text-right whitespace-nowrap">
                      <button onClick={() => downloadPdf(inv.id, inv.number)} className="btn-ghost text-xs" data-testid={`invoice-pdf-desktop-${inv.number}`}>
                        <Download className="inline w-3.5 h-3.5 mr-1" strokeWidth={1.8} />
                        PDF
                      </button>
                      <button onClick={() => deleteInvoice(inv.id)} className="ml-2 p-2 rounded-full hover:bg-sage-light" title="Supprimer" data-testid={`invoice-delete-desktop-${inv.number}`}>
                        <Trash2 className="w-3.5 h-3.5 text-muted-soft" strokeWidth={1.6} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {showCreate && (
        <Modal onClose={() => setShowCreate(false)} title="Nouvelle facture" testid="create-invoice-modal">
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium block mb-2">Client</label>
              <select className="input-soft" value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })} data-testid="invoice-client">
                {clients.map((c) => <option key={c.id} value={c.id}>{c.is_business ? `${c.company_name || c.name} (pro)` : c.name}</option>)}
              </select>
            </div>

            <div>
              <label className="text-sm font-medium block mb-2">
                Date de la prestation <span className="text-muted-soft text-xs font-normal">— facultatif, sinon = date d'émission (conforme art. 242 nonies A CGI)</span>
              </label>
              <input type="date" className="input-soft"
                value={form.service_date}
                onChange={(e) => setForm({ ...form, service_date: e.target.value })}
                data-testid="invoice-service-date" />
            </div>

            <div className="space-y-2">
              <div className="text-sm font-medium">Lignes</div>
              {form.items.map((item, i) => (
                <div key={item._key} className="grid grid-cols-12 gap-2">
                  <input className="input-soft col-span-6" placeholder="Désignation" value={item.label} onChange={(e) => setItem(i, "label", e.target.value)} data-testid={`invoice-item-label-${i}`} />
                  <input type="number" min="1" className="input-soft col-span-2" placeholder="Qté" value={item.quantity} onChange={(e) => setItem(i, "quantity", e.target.value)} data-testid={`invoice-item-qty-${i}`} />
                  <input type="number" step="0.01" className="input-soft col-span-3" placeholder="PU €" value={item.unit_price} onChange={(e) => setItem(i, "unit_price", e.target.value)} data-testid={`invoice-item-price-${i}`} />
                  <button onClick={() => removeItem(i)} className="col-span-1 rounded-xl hover:bg-sage-light" disabled={form.items.length === 1}>
                    <X className="w-4 h-4 mx-auto" />
                  </button>
                </div>
              ))}
              <button onClick={addItem} className="text-xs text-sage hover:underline" data-testid="invoice-add-item">+ Ajouter une ligne</button>
            </div>

            <div>
              <label className="text-sm font-medium block mb-2">Notes (optionnel)</label>
              <textarea className="input-soft" style={{ height: 70, paddingTop: 12 }} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="invoice-notes" />
            </div>

            <div className="flex items-center justify-between pt-2 border-t" style={{ borderColor: "var(--border)" }}>
              <div className="text-sm text-muted-soft">Total</div>
              <div className="font-display text-2xl">{euro(total)}</div>
            </div>

            <div className="text-xs text-muted-soft">Mention automatique : <i>TVA non applicable, art. 293 B CGI.</i></div>

            {error && <div className="text-sm text-clay">{error}</div>}

            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setShowCreate(false)} className="btn-ghost text-sm" data-testid="invoice-cancel">Annuler</button>
              <button onClick={create} className="btn-sage text-sm" data-testid="invoice-save">Émettre la facture</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

function Modal({ onClose, title, children, testid }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid={testid}>
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-full max-w-2xl card-soft p-6 max-h-[90vh] overflow-y-auto scroll-soft">
        <div className="flex items-center justify-between mb-4">
          <div className="font-display text-xl">{title}</div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-sage-light"><X className="w-4 h-4" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}
